/**
 * Journal form templates — one UX layout per document-type family.
 * Map AccDocumentType.code → template; add new types here or later via DB field.
 */
export type DocumentFormTemplateId = 'general' | 'payment' | 'receipt' | 'invoice';

export type DocumentFormTemplate = {
  id: DocumentFormTemplateId;
  label: string;
  description: string;
  hints: string[];
  /** Optional dedicated entry route (e.g. payment workspace). */
  entryRoute?: string;
  /** Optional dedicated edit route when document uses a separate form. */
  editRoute?: (id: number) => string;
  /** Open line details (link, due, P&L) by default for this template. */
  defaultExtendedLines: boolean;
  /** Highlight open-item link picker on AP/AR lines. */
  emphasizeLinkPicker: boolean;
  /** Suggested minimum line rows when starting a new document. */
  suggestedLineCount: number;
};

export const DOCUMENT_FORM_TEMPLATES: Record<DocumentFormTemplateId, DocumentFormTemplate> = {
  general: {
    id: 'general',
    label: 'General journal',
    description: 'Free-form multi-line entry for adjustments, accruals, and manual postings.',
    hints: [
      'Add as many lines as needed — debits must equal credits per company.',
      'Use line details for link #, due date, and P&L period when required.',
    ],
    defaultExtendedLines: false,
    emphasizeLinkPicker: false,
    suggestedLineCount: 2,
  },
  payment: {
    id: 'payment',
    label: 'Payment',
    description: 'Pay suppliers or clear open payable items — credit AP, debit bank or cash.',
    hints: [
      'Use Accounting → Payments for guided bank + invoice entry.',
      'Select AP account and pick open invoices; bank amount must match invoice total.',
    ],
    defaultExtendedLines: true,
    emphasizeLinkPicker: true,
    suggestedLineCount: 2,
    entryRoute: '/accounting/payments/new',
    editRoute: (id) => `/accounting/payments/${id}/edit`,
  },
  receipt: {
    id: 'receipt',
    label: 'Receipt',
    description: 'Collect from customers or clear open receivables — debit AR, credit bank or cash.',
    hints: [
      'Select an AR account with open items, then use the link icon to pick outstanding charges.',
      'Add a second line for the bank or cash account (credit side).',
    ],
    defaultExtendedLines: true,
    emphasizeLinkPicker: true,
    suggestedLineCount: 2,
  },
  invoice: {
    id: 'invoice',
    label: 'Invoice',
    description: 'Quick purchase invoice — vendor, amount, expense; AP credit line is created automatically.',
    hints: [
      'Select vendor (AP), expense account, and amount.',
      'Due date defaults from vendor payment terms; DN/link optional for matching.',
    ],
    defaultExtendedLines: true,
    emphasizeLinkPicker: false,
    suggestedLineCount: 1,
    entryRoute: '/accounting/invoices/new',
    editRoute: (id) => `/accounting/invoices/${id}/edit`,
  },
};

/** Purchase-side invoice types that use the short invoice form (expense Dr + AP Cr). */
export const PURCHASE_INVOICE_TYPE_CODES = [
  'PI',
  'DA',
  'PDA',
  'FDA',
  'FRE',
  'DEM',
  'BUN',
  'COM',
] as const;

/** Map document type codes to form templates. Unknown codes fall back to general. */
export const DOCUMENT_TYPE_CODE_TO_TEMPLATE: Record<string, DocumentFormTemplateId> = {
  JOURNAL: 'general',
  BANK: 'general',
  PROV: 'general',
  IC: 'general',
  CLOSE: 'general',
  PAYMENT: 'payment',
  RECEIPT: 'receipt',
  PI: 'invoice',
  SI: 'invoice',
  CN: 'invoice',
  DN: 'invoice',
  DA: 'invoice',
  PDA: 'invoice',
  FDA: 'invoice',
  FRE: 'invoice',
  DEM: 'invoice',
  BUN: 'invoice',
  COM: 'invoice',
};

export type DocumentTypeLike = { code: string; name?: string } | null | undefined;

export function isPurchaseInvoiceType(documentType: DocumentTypeLike): boolean {
  const code = documentType?.code?.trim().toUpperCase() ?? '';
  return (PURCHASE_INVOICE_TYPE_CODES as readonly string[]).includes(code);
}

export function resolveDocumentFormTemplate(documentType: DocumentTypeLike): DocumentFormTemplate {
  const code = documentType?.code?.trim().toUpperCase() ?? '';
  const templateId = DOCUMENT_TYPE_CODE_TO_TEMPLATE[code] ?? 'general';
  const base = DOCUMENT_FORM_TEMPLATES[templateId];
  // Dedicated short form only for purchase AP invoices — SI/CN/DN stay on journal layout.
  if (templateId === 'invoice' && !isPurchaseInvoiceType(documentType)) {
    const { entryRoute: _e, editRoute: _r, ...rest } = base;
    return rest;
  }
  return base;
}

export function resolveDocumentFormTemplateId(documentType: DocumentTypeLike): DocumentFormTemplateId {
  return resolveDocumentFormTemplate(documentType).id;
}

export function resolveDocumentEditRoute(
  documentType: DocumentTypeLike,
  id: number,
  defaultRoute: string,
): string {
  const template = resolveDocumentFormTemplate(documentType);
  return template.editRoute?.(id) ?? defaultRoute;
}

export function resolveDocumentEntryRoute(documentType: DocumentTypeLike): string | undefined {
  return resolveDocumentFormTemplate(documentType).entryRoute;
}
